import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../context/AuthContext";
import {
  canFinalizarForno,
  canVerTodasLojasUser,
} from "../../../lib/screenAccess";
import { useFornoEvents } from "../../../hooks/useFornoEvents";
import { fornoService, FornoItem } from "../../../services/fornoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { ITEMS_POR_COLUNA } from "../constants/forneiro";
import { broadcastDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import { broadcastPainelRefresh } from "../../../utils/painelRefreshBroadcast";

type MoveDirection = "esquerda" | "direita";

type LojaOption = { value: number; label: string };

interface ForneiroContextData {
  items: FornoItem[];
  colunas: FornoItem[][];
  loading: boolean;
  refreshing: boolean;
  finalizandoId: number | null;
  tick: number;
  fetchItems: () => Promise<void>;
  refreshNow: () => Promise<void>;
  handleFinalizar: (itemId: number) => Promise<void>;
  handleMover: (itemId: number, direcao: MoveDirection) => void;
  canFinalizar: boolean;
  showLojaSelector: boolean;
  lojaOptions: LojaOption[];
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
}

const ForneiroContext = createContext<ForneiroContextData | undefined>(
  undefined
);

const agruparEmColunas = (items: FornoItem[]): FornoItem[][] => {
  const colunas: FornoItem[][] = [];
  for (let i = 0; i < items.length; i += ITEMS_POR_COLUNA) {
    colunas.push(items.slice(i, i + ITEMS_POR_COLUNA));
  }
  return colunas;
};

const useRealtimeTick = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const interval = setInterval(bump, 1000);

    const onVisible = () => {
      if (document.visibilityState === "visible") bump();
    };
    const onFocus = () => bump();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return tick;
};

export const ForneiroProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [items, setItems] = useState<FornoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [finalizandoId, setFinalizandoId] = useState<number | null>(null);
  const isFetchingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const [sseAvailable, setSseAvailable] = useState(true);
  const tick = useRealtimeTick();

  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const canFinalizar = canFinalizarForno(user);

  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );

  const effectiveLojaId = podeVerTodasLojas
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;

  const lojaId = effectiveLojaId;

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
    enabled: podeVerTodasLojas,
  });

  const lojaOptions = useMemo(
    () =>
      (lojas as Loja[]).map((loja) => ({
        value: loja.id,
        label: loja.nome,
      })),
    [lojas]
  );

  const fetchItems = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (!hasLoadedRef.current) setLoading(true);

    try {
      const response = await fornoService.listar(lojaId);
      setItems(response.items);
    } catch {
    } finally {
      if (!hasLoadedRef.current) {
        setLoading(false);
        hasLoadedRef.current = true;
      }
      isFetchingRef.current = false;
    }
  }, [lojaId]);

  const refreshNow = useCallback(async () => {
    if (isFetchingRef.current) return;
    setRefreshing(true);
    try {
      await fetchItems();
    } finally {
      setRefreshing(false);
    }
  }, [fetchItems]);

  useFornoEvents(fetchItems, lojaId ?? null, {
    onAvailabilityChange: setSseAvailable,
  });

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (sseAvailable) return;

    const interval = setInterval(() => {
      void fetchItems();
    }, 60_000);

    return () => clearInterval(interval);
  }, [sseAvailable, fetchItems]);

  const handleFinalizar = useCallback(
    async (itemId: number) => {
      setFinalizandoId(itemId);
      try {
        await fornoService.finalizar(itemId);
        broadcastPainelRefresh();
        broadcastDashboardRefresh();
        toast.success("Item finalizado!");
        await fetchItems();
      } catch {
        toast.error("Erro ao finalizar item");
      } finally {
        setFinalizandoId(null);
      }
    },
    [fetchItems]
  );

  const handleMover = useCallback((itemId: number, direcao: MoveDirection) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === itemId);
      if (idx === -1) return prev;

      const targetIdx = direcao === "esquerda" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;

      const newItems = [...prev];
      [newItems[idx], newItems[targetIdx]] = [
        newItems[targetIdx],
        newItems[idx],
      ];
      return newItems;
    });
  }, []);

  const colunas = useMemo(() => agruparEmColunas(items), [items]);

  const value = useMemo(
    () => ({
      items,
      colunas,
      loading,
      refreshing,
      finalizandoId,
      tick,
      fetchItems,
      refreshNow,
      handleFinalizar,
      handleMover,
      canFinalizar,
      showLojaSelector: podeVerTodasLojas,
      lojaOptions,
      selectedLojaId,
      setSelectedLojaId,
    }),
    [
      items,
      colunas,
      loading,
      refreshing,
      finalizandoId,
      tick,
      fetchItems,
      refreshNow,
      handleFinalizar,
      handleMover,
      canFinalizar,
      lojaOptions,
      selectedLojaId,
      podeVerTodasLojas,
    ]
  );

  return (
    <ForneiroContext.Provider value={value}>
      {children}
    </ForneiroContext.Provider>
  );
};

export const useForneiro = () => {
  const context = useContext(ForneiroContext);
  if (!context) {
    throw new Error("useForneiro must be used within ForneiroProvider");
  }
  return context;
};
